import {
  ArrowRightOutline,
  DropdownInput,
  DropdownOption,
  Label,
  SearchDropdownInput,
  formatNumber,
  individualFullName,
  modalStyles,
  useToast,
} from '@aslanpay/portal-common-lib';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';

import MoneyAmountInput from 'app/components/UI/Inputs/MoneyAmountInput/MoneyAmountInput';
import getPointsValue, { getPointsFromValue } from 'app/utils/points';
import { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, Resolver, useForm } from 'react-hook-form';
import useGroupsWithPot from 'app/hooks/useGroupsWithPot/useGroupsWithPot';
import usePots, { companyPotQueryParams } from 'app/hooks/usePots';
import useFetchCompany from 'app/hooks/useFetchCompany';
import CenterSpinner from 'app/components/UI/CenterSpinner';
import useGroups from 'app/hooks/useGroups';
import useCreatePotWithPotManagersAndBalance from 'app/hooks/usePots/useCreatePotWithPotManagersAndBalance';
import FailedMembersSummary from 'app/components/GroupMemberships/FailedMembersSummary';
import { FailedMember } from 'app/utils/groupMembership';
import useInfiniteFetchEmployees from 'app/hooks/useInfiniteFetchEmployees';

import CompanyWalletInformation from '../CompanyWalletInformation/CompanyWalletInformation';

import {
  CreatePotFormFields,
  DEFAULT_CREATE_POT_FORM_DATA,
  CreatePotFormProps,
  createSchema,
} from './CreatePotForm.types';
import { formatCreatePotRequestFromForm, formatOptionsForDropdown } from './CreatePotForm.function';

const PAGE_SIZE = 20;

function CreatePotForm({ onClose, onSuccess, companyId, predefinedGroup }: CreatePotFormProps) {
  const isSingleCreatePot = !!predefinedGroup;

  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNamesMap, setSelectedNamesMap] = useState<Map<string, string>>(new Map());

  // Fetch company data
  const { data: companyData, isLoading: isLoadingCompany } = useFetchCompany(companyId);

  // Fetch company points bag
  const { data: companyPots, isLoading: isLoadingCompanyPot } = usePots.useFetchPots(
    companyPotQueryParams(companyId)
  );

  const companyPot = companyPots?.list?.[0];
  const currentBalance = companyPot?.balance || 0;
  const currentBalanceValue = getPointsValue(currentBalance);

  const schema = useMemo(() => createSchema(currentBalanceValue), [currentBalanceValue]);

  const methods = useForm<CreatePotFormFields>({
    resolver: yupResolver(schema) as Resolver<CreatePotFormFields>,
    mode: 'onChange',
    defaultValues: DEFAULT_CREATE_POT_FORM_DATA,
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid, isDirty },
  } = methods;

  const watchedGroup = watch('group');
  const amount = watch('amount');

  useEffect(() => {
    if (amount) {
      trigger('amount');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, trigger]);

  const { flatGroupsWithPots, isLoading } = useGroupsWithPot(companyId, !predefinedGroup);

  const groups = useMemo(
    () => (predefinedGroup ? [predefinedGroup] : flatGroupsWithPots),
    [predefinedGroup, flatGroupsWithPots]
  );

  const groupId =
    typeof watchedGroup === 'object' && watchedGroup !== null ? watchedGroup : watchedGroup;

  // Fetch existing group memberships to pre-select POT_MANAGERs and to determine POST vs PUT on submit
  const { data: membershipPages, isLoading: isLoadingMembers } = useGroups.useFetchGroupMemberships(
    companyId,
    String(groupId),
    undefined,
    !!groupId
  );

  const members = useMemo(
    () => membershipPages?.pages.flatMap((p) => p.list) ?? [],
    [membershipPages]
  );

  const memberMap = useMemo(() => new Map(members.map((m) => [m.individualId, m])), [members]);

  // Fetch all company employees with infinite scroll
  const {
    data: employeePages,
    isFetching: isFetchingEmployees,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteFetchEmployees({
    companyId,
    query: searchQuery.trim() || undefined,
    status: ['ACTIVE', 'CREATED', 'INVITED'],
    size: PAGE_SIZE,
    sort: 'ASC',
    sortBy: 'firstName',
    enabled: !!companyId && !!groupId,
  });

  const memberOptions: DropdownOption[] = useMemo(() => {
    const allEmployees = employeePages?.pages.flatMap((p) => p.list) ?? [];
    return allEmployees.map((e) => ({
      value: e.id,
      text: individualFullName(e),
      email: e.email,
      wasPreviouslyAPotManager: memberMap.get(e.id)?.membershipRole === 'POT_MANAGER',
    }));
  }, [employeePages, memberMap]);

  const { execute: createPotWithManagersAndBalance } = useCreatePotWithPotManagersAndBalance(
    companyId,
    String(watchedGroup),
    toast
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [failedMembers, setFailedMembers] = useState<FailedMember[]>([]);

  const handleCreatePot = (data: CreatePotFormFields, onAfterSuccess = () => {}) => {
    setIsSubmitting(true);
    setFailedMembers([]);
    createPotWithManagersAndBalance(
      {
        ...formatCreatePotRequestFromForm({
          data,
          members,
          companyPot,
          groups,
        }),
        memberNamesById: selectedNamesMap,
      },
      {
        onSuccess: (result) => {
          setIsSubmitting(false);
          // The pot was created; if some managers couldn't be added, refresh the pot list but keep the modal
          // open so the admin can see which managers failed and why.
          if (result?.failedMembers?.length) {
            setFailedMembers(result.failedMembers);
            onSuccess?.();
            return;
          }
          onAfterSuccess();
        },
        onError: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  const handleCancel = () => {
    onClose();
  };

  const handleConfirm = (data: CreatePotFormFields) => {
    handleCreatePot(data, () => {
      onSuccess?.();
      onClose();
    });
  };

  const handleConfirmAndCreateMore = (data: CreatePotFormFields) => {
    handleCreatePot(data, () => {
      onSuccess?.();
      methods.reset(DEFAULT_CREATE_POT_FORM_DATA);
      setSelectedNamesMap(new Map());
      setSearchQuery('');
      setResetKey((k) => k + 1);
    });
  };

  const amountNumber = parseFloat(amount || '0');
  const pointsAmount = getPointsFromValue(amountNumber);
  const conversionRateText = `Rate: ${formatNumber(getPointsFromValue(1))} points = £1`;

  const options: DropdownOption[] = useMemo(() => formatOptionsForDropdown(groups), [groups]);

  useEffect(() => {
    if (predefinedGroup) {
      setValue('group', String(options[0].value), { shouldValidate: true, shouldDirty: true });
    }
  }, [predefinedGroup, setValue, options]);

  useEffect(() => {
    if (!watchedGroup) {
      setValue('potManagers', [], { shouldValidate: true, shouldDirty: true });
      setSelectedNamesMap(new Map());
      return;
    }
    const preSelected = memberOptions.filter((m) => m.wasPreviouslyAPotManager);
    setValue(
      'potManagers',
      preSelected.map((m) => String(m.value)),
      { shouldValidate: true, shouldDirty: true }
    );
    setSelectedNamesMap(new Map(preSelected.map((m) => [String(m.value), m.text])));
  }, [memberOptions, watchedGroup, setValue]);

  if (isLoadingCompany || isLoadingCompanyPot || isLoading) {
    return <CenterSpinner />;
  }

  return (
    <FormProvider {...methods}>
      <VStack spacing={6} align="stretch" maxW="100%" w="100%">
        {/* Company Wallet Information */}
        <CompanyWalletInformation company={companyData} currentBalance={currentBalance} />

        <Flex key={resetKey} gap="20px" alignItems="flex-start" flexDir="column">
          <FormControl isInvalid={!!errors?.group?.message} isRequired>
            <FormLabel>Pride</FormLabel>
            <Controller
              control={control}
              name="group"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="Select a pride"
                  defaultValue={options.find((group) => String(group.value) === String(value))}
                  options={options}
                  onChange={(option) => onChange(option != null ? String(option) : undefined)}
                  onBlur={onBlur}
                  isSearchable
                  isReadOnly={isSingleCreatePot}
                  renderText={(option) => {
                    return (
                      <Flex direction="row" flex="1">
                        <Text as="p" textStyle="smallMedium">
                          {option.text}
                        </Text>
                        {option.hasPot && (
                          <>
                            <Spacer />
                            <Text as="span">Already has pot</Text>
                          </>
                        )}
                      </Flex>
                    );
                  }}
                />
              )}
            />
            <FormErrorMessage>{errors?.group?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.potManagers?.message} isRequired>
            <FormLabel>Pot manager(s)</FormLabel>
            <Controller
              control={control}
              name="potManagers"
              render={({ field: { onChange, value } }) => {
                const selected = Array.isArray(value) ? value : [];
                return (
                  <Flex direction="column" gap="8px">
                    {selected.length > 0 && (
                      <Flex gap="6px" flexWrap="wrap">
                        {selected.map((id) => (
                          <Label
                            size="sm"
                            key={id}
                            label={selectedNamesMap.get(id) ?? id}
                            onClose={() => {
                              const next = selected.filter((v) => v !== id);
                              onChange(next);
                              setSelectedNamesMap((prev) => {
                                const m = new Map(prev);
                                m.delete(id);
                                return m;
                              });
                            }}
                          />
                        ))}
                      </Flex>
                    )}
                    <SearchDropdownInput
                      placeholder="Search employees"
                      itemName="employee(s)"
                      options={memberOptions}
                      selected={selected}
                      onChange={(newSelected) => {
                        onChange(newSelected);
                        setSelectedNamesMap((prev) => {
                          const next = new Map(prev);
                          newSelected.forEach((id) => {
                            if (!next.has(id)) {
                              const opt = memberOptions.find((o) => String(o.value) === id);
                              if (opt) next.set(id, opt.text);
                            }
                          });
                          prev.forEach((_, id) => {
                            if (!newSelected.includes(id)) next.delete(id);
                          });
                          return next;
                        });
                      }}
                      onSearch={setSearchQuery}
                      searchDebounceDelay={300}
                      isLoading={isFetchingEmployees && !isFetchingNextPage}
                      isLoadingMore={isFetchingNextPage}
                      onLoadMore={hasNextPage && !isFetchingNextPage ? fetchNextPage : undefined}
                      isDisabled={isLoadingMembers || !groupId}
                      renderText={(option) => (
                        <Flex direction="column" flex="1">
                          <Flex direction="row" flex="1">
                            <Text as="p" textStyle="smallMedium">
                              {option.text}
                            </Text>
                            <Spacer />
                            <Text as="span" textStyle="smallRegular">
                              {option.email}
                            </Text>
                          </Flex>
                        </Flex>
                      )}
                    />
                  </Flex>
                );
              }}
            />
            <FormErrorMessage>{errors?.potManagers?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.amount} isRequired flex={1}>
            <FormLabel>Amount (points worth in £)</FormLabel>
            <Box position="relative">
              <MoneyAmountInput
                value={watch('amount') || ''}
                onChange={(value) => setValue('amount', value, { shouldValidate: true })}
                placeholder="Amount (points worth)"
                currency="GBP"
                maxDecimalPlaces={0}
                isInvalid={!!errors.amount}
              />
            </Box>
            <FormErrorMessage>{errors?.amount?.message as string}</FormErrorMessage>
          </FormControl>

          <HStack justify="space-between" w="full">
            <Text textStyle="smallRegular" color="brand.mediumGray2">
              {formatNumber(pointsAmount)} points
            </Text>
            <Text textStyle="smallRegular" color="brand.mediumGray2">
              {conversionRateText}
            </Text>
          </HStack>

          <FailedMembersSummary
            failedMembers={failedMembers}
            alertTitle="The pot was created, but some managers could not be added"
          />
        </Flex>

        <Text color="system.error" textStyle="extraSmallMedium">
          * mandatory
        </Text>

        {/* Footer Buttons */}
        <HStack spacing={3} justify="flex-end" p={modalStyles.baseStyle.footer.p}>
          {isSingleCreatePot && (
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
          )}

          {!isSingleCreatePot && (
            <Button
              variant="outline"
              isLoading={isSubmitting}
              isDisabled={!isValid || !isDirty}
              onClick={() => handleSubmit(handleConfirmAndCreateMore)()}
            >
              Confirm & create more
            </Button>
          )}

          <Button
            type="submit"
            colorScheme="purple"
            rightIcon={<ArrowRightOutline />}
            isLoading={isSubmitting}
            isDisabled={!isValid || !isDirty}
            onClick={() => {
              handleSubmit(handleConfirm)();
            }}
          >
            Confirm
          </Button>
        </HStack>
      </VStack>
    </FormProvider>
  );
}

export default CreatePotForm;
